import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, ShieldCheck, Award, MapPin, Quote } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Breadcrumbs from "../components/Breadcrumbs";
import SEO from "../components/SEO";
import CalculatorCallout from "../components/CalculatorCallout";
import { team } from "../data/team";
import { businessInfo, stats } from "../mock";
import { localBusinessSchema, breadcrumbSchema, personSchema } from "../utils/schemas";

const AboutPage = () => {
  const schemas = [
    localBusinessSchema(),
    breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Meet The Beasts", path: "/about" },
    ]),
    // Person schema for each team member (helps Google Knowledge Graph)
    ...team.map((m) =>
      personSchema({
        name: m.name,
        role: m.role,
        slug: m.slug,
        image: m.photo,
        description: Array.isArray(m.bio) ? m.bio.join(" ") : m.bio,
      })
    ),
  ];

  return (
    <div className="bg-black min-h-screen text-white">
      <SEO
        title="Meet The Beasts | Joe & Victoria — Claim Beast Public Adjusters"
        description="Meet Joe and Victoria — the licensed public adjusters behind Claim Beast. Based in Riverview, FL, fighting for Tampa Bay homeowners with denied & underpaid insurance claims."
        path="/about"
        keywords="meet the beasts, joe claim beast, victoria claim beast, claim beast team, tampa bay public adjuster team, riverview public adjuster"
        schemas={schemas}
      />
      <Header />
      <Breadcrumbs items={[{ name: "Meet The Beasts", path: "/about" }]} />

      {/* Hero */}
      <section className="relative bg-black py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(225,29,38,0.15),transparent_55%)]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-3 mb-4">
            <span className="h-[2px] w-10 bg-[#E11D26]" />
            <span className="text-[#E11D26] text-xs sm:text-sm font-bold tracking-[0.25em] uppercase">
              The Team Behind Claim Beast
            </span>
          </div>
          <h1
            className="text-white text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black uppercase leading-[0.92]"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            Meet The <span className="text-[#E11D26]">Beasts</span>.
          </h1>
          <p className="mt-6 text-white/80 text-base sm:text-lg max-w-2xl leading-relaxed">
            Two licensed public adjusters. One mission: recover every dollar
            Tampa Bay homeowners are owed. Headquartered right here in
            Riverview, FL.
          </p>
        </div>
      </section>

      {/* Stats bar */}
      <div className="border-y border-white/10 bg-black/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-3 divide-x divide-white/10">
          {stats.map((s, i) => (
            <div key={i} className="py-7 text-center">
              <div
                className="text-2xl sm:text-4xl font-black text-[#E11D26]"
                style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
              >
                {s.value}
              </div>
              <div className="mt-1 text-white/70 text-[10px] sm:text-xs font-bold tracking-[0.3em]">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Team members */}
      <section className="py-16 md:py-24 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 space-y-20">
          {team.map((m, idx) => (
            <article
              key={m.slug}
              className={`grid lg:grid-cols-12 gap-10 items-center ${
                idx % 2 === 1 ? "lg:[&>div:first-child]:order-2" : ""
              }`}
            >
              <div className="lg:col-span-5">
                <div className="relative">
                  <div className="absolute -inset-2 bg-[#E11D26] -rotate-2" />
                  <img
                    src={m.photo}
                    alt={`${m.name} - ${m.role} at Claim Beast Public Adjusters in Riverview FL`}
                    className="relative w-full aspect-[4/5] object-cover grayscale hover:grayscale-0 transition-all duration-500"
                  />
                  <div className="absolute bottom-3 left-3 right-3 bg-black/85 backdrop-blur-sm px-4 py-3 border-l-4 border-[#E11D26]">
                    <div
                      className="text-white text-2xl font-black uppercase"
                      style={{
                        fontFamily: "'Anton', 'Oswald', sans-serif",
                      }}
                    >
                      {m.name}
                    </div>
                    <div className="text-[#E11D26] text-xs font-bold tracking-[0.2em] uppercase">
                      {m.role}
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-7">
                <div className="flex items-center gap-3 mb-3">
                  <span className="h-[2px] w-10 bg-[#E11D26]" />
                  <span className="text-[#E11D26] text-xs font-bold tracking-[0.25em] uppercase">
                    {m.yearsExperience} years experience
                  </span>
                </div>
                <h2
                  className="text-white text-4xl sm:text-5xl font-black uppercase leading-[0.95]"
                  style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
                >
                  {m.name}.
                </h2>
                <div className="mt-5 space-y-4">
                  {m.bio.map((p, i) => (
                    <p
                      key={i}
                      className="text-white/80 leading-relaxed text-base"
                    >
                      {p}
                    </p>
                  ))}
                </div>

                {/* Quote */}
                <div className="mt-7 relative bg-[#0F0F0F] border-l-4 border-[#E11D26] p-6">
                  <Quote className="absolute top-3 right-3 h-8 w-8 text-[#E11D26]/30" />
                  <p className="text-white italic leading-relaxed pr-10">
                    &ldquo;{m.quote}&rdquo;
                  </p>
                  <div className="mt-3 text-[#E11D26] text-xs font-bold tracking-[0.2em] uppercase">
                    — {m.name}
                  </div>
                </div>

                {/* Specialties + licenses */}
                <div className="mt-7 grid sm:grid-cols-2 gap-4">
                  <div className="bg-[#0F0F0F] border border-white/10 p-5">
                    <div className="flex items-center gap-2 text-[#E11D26] text-xs font-bold tracking-[0.2em] uppercase">
                      <Award className="h-4 w-4" /> Specialties
                    </div>
                    <ul className="mt-3 space-y-1.5">
                      {m.specialties.map((s, i) => (
                        <li
                          key={i}
                          className="text-white/80 text-sm leading-relaxed flex gap-2"
                        >
                          <span className="text-[#E11D26]">▸</span>
                          {s}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-[#0F0F0F] border border-white/10 p-5">
                    <div className="flex items-center gap-2 text-[#E11D26] text-xs font-bold tracking-[0.2em] uppercase">
                      <ShieldCheck className="h-4 w-4" /> Licensed
                    </div>
                    <ul className="mt-3 space-y-1.5 text-white/80 text-sm">
                      <li>
                        <span className="text-white/55">FL License:</span> #
                        {m.licenses.florida}
                      </li>
                      {m.licenses.georgia && (
                        <li>
                          <span className="text-white/55">GA License:</span> #
                          {m.licenses.georgia}
                        </li>
                      )}
                      <li className="flex items-center gap-2">
                        <MapPin className="h-3.5 w-3.5 text-[#E11D26]" />
                        Riverview, FL · Tampa Bay
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 bg-[#E11D26]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <h3
            className="text-white text-2xl sm:text-3xl md:text-4xl font-black uppercase leading-tight"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            Ready to put Joe &amp; Victoria on your claim?
          </h3>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to="/#contact"
              className="group inline-flex items-center gap-3 bg-black hover:bg-[#0A0A0A] text-white font-bold uppercase tracking-wider px-7 py-4 transition-colors"
            >
              <span className="text-sm">Free Claim Review</span>
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Link>
            <a
              href={`tel:${businessInfo.phoneRaw}`}
              className="inline-flex items-center justify-center gap-2 bg-white text-black hover:bg-white/90 font-bold uppercase tracking-wider px-7 py-4 transition-colors"
            >
              <Phone className="h-4 w-4" />
              <span className="text-sm">{businessInfo.phone}</span>
            </a>
          </div>
        </div>
      </section>

      <CalculatorCallout
        eyebrow="Curious What We'd Fight For?"
        headline="Put A Number On Your Claim Before You Even Meet Us."
        body="The same playbook we use to recover six-figure claims for Tampa Bay homeowners is built into our free calculator. Walk through a few questions about your damage — we'll show you what the math says, then follow up to talk specifics."
        ctaLabel="See My Estimate"
      />

      <Footer />
    </div>
  );
};

export default AboutPage;
